import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Share2, Menu as MenuIcon } from 'lucide-react';
import { SettingsSidebar } from './SettingsSidebar';
import { api, UserProfile, MarketProfile, PortfolioItem } from '../../lib/api';
import { PortfolioSwipeCardModal } from './PortfolioSwipeCardModal';
import { getTelegramWebApp } from '../../lib/telegram';
import { PortfolioMiniCard } from './PortfolioMiniCard';

interface ProfileViewProps {
  profile: UserProfile;
  onEdit: () => void;
  onDeleteProfile?: () => void;
  referralLink?: string;
  onPrivacy?: () => void;
  onVerification?: () => void;
  onWrite?: (item: PortfolioItem) => void;
}

const ROLE_LABELS: Record<string, string> = {
  listener: 'Слухач',
  beatmaker: 'Бітмейкер',
  producer: 'Продюсер',
  designer: 'Дизайнер',
  performer: 'Виконавець',
  director: 'Режисер',
};

export function ProfileView({
  profile,
  onEdit,
  onDeleteProfile,
  referralLink,
  onPrivacy,
  onVerification,
  onWrite,
}: ProfileViewProps) {
  const [showSettings, setShowSettings] = useState(false);
  const [marketProfile, setMarketProfile] = useState<MarketProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState<PortfolioItem | null>(null);

  const effectiveProfile = marketProfile ?? profile;
  
  useEffect(() => {
    let cancelled = false;

    const hydrateProfile = async () => {
      if (!profile?.telegram_id) {
        setMarketProfile(null);
        return;
      }

      setIsLoadingProfile(true);
      try {
        const response = await api.market.getProfile(profile.telegram_id);
        if (!cancelled) {
          setMarketProfile(response);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch marketplace profile view:', error);
          setMarketProfile(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingProfile(false);
        }
      }
    };

    hydrateProfile();

    return () => {
      cancelled = true;
    };
  }, [profile.telegram_id]);

  const rolesArray = useMemo(() => {
    const rolesValue = effectiveProfile.roles;
    if (!rolesValue) return [];
    return String(rolesValue)
      .split(',')
      .map((role) => role.trim())
      .filter(Boolean);
  }, [effectiveProfile.roles]);

  const hashtags = useMemo(() => {
    const stylesValue = effectiveProfile.styles;
    if (!stylesValue || stylesValue === 'Не вказано') return [];
    return stylesValue
      .split(',')
      .map((style) => style.trim())
      .filter(Boolean)
      .map((style) => (style.startsWith('#') ? style : `#${style}`));
  }, [effectiveProfile.styles]);

  const portfolios = useMemo(() => {
    if (marketProfile && Array.isArray(marketProfile.portfolios)) {
      return marketProfile.portfolios;
    }
    return profile.portfolios ?? [];
  }, [marketProfile, profile.portfolios]);

  const stats = marketProfile?.stats;
  const totalWorks = portfolios.length;
  const totalLikes = stats?.total_likes ?? stats?.profile_likes ?? 0;
  const followers = stats?.followers ?? 0;
  const isBoosted = effectiveProfile.is_boosted && effectiveProfile.boost_expires_at && new Date(effectiveProfile.boost_expires_at) > new Date();

  const handleShareProfile = async () => {
    const tg = getTelegramWebApp();

    // Prefer backend-generated referral/profile link when available
    let urlToShare = referralLink;

    if (!urlToShare) {
      try {
        const referral = await api.referral.get();
        urlToShare = referral.link;
      } catch (error) {
        console.warn('Failed to fetch referral link for sharing, falling back to current URL:', error);
        if (typeof window !== 'undefined') {
          urlToShare = window.location.href;
        }
      }
    }

    if (!urlToShare) {
      console.warn('No URL available for sharing.');
      return;
    }

    const displayName =
      effectiveProfile.name ||
      effectiveProfile.username ||
      'Splyt creator';

    const text = `Переглянь мій профіль у Splyt: ${displayName}`;

    const encodedUrl = encodeURIComponent(urlToShare);
    const encodedText = encodeURIComponent(text);

    if (tg?.openTelegramLink) {
      tg.openTelegramLink(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`);
      return;
    }

    if (typeof navigator !== 'undefined' && (navigator as any).share) {
      try {
        await (navigator as any).share({
          title: 'Splyt',
          text,
          url: urlToShare,
        });
        return;
      } catch {
        // Ignore share cancellation and fall through to window.open
      }
    }

    if (typeof window !== 'undefined') {
      window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, '_blank');
    }
  };

  return (
    <div className="pb-8 relative">
      {/* Settings Sidebar */}
      <SettingsSidebar 
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onEditProfile={onEdit}
        onDeleteProfile={onDeleteProfile}
        onPrivacy={onPrivacy}
        onVerification={onVerification}
      />

      {/* Header Buttons */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <button
          onClick={handleShareProfile}
          className="w-10 h-10 rounded-full border backdrop-blur-xl flex items-center justify-center transition-all hover:border-white/30"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.12)',
          }}
        >
          <Share2 className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
        </button>
        <button
          onClick={() => setShowSettings(true)}
          className="w-10 h-10 rounded-full border backdrop-blur-xl flex items-center justify-center transition-all hover:border-white/30"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.12)',
          }}
        >
          <MenuIcon className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
        </button>
      </div>

      {/* Profile Section - Centered */}
      <div className="flex flex-col items-center pt-4 pb-6 px-4">
        {/* Large Profile Photo with Star Overlay */}
        <div className="relative mb-4">
          <div 
            className="w-40 h-40 rounded-full bg-gradient-to-br from-[#00FF88] via-[#4DD4FF] to-[#0066CC] border-0 flex items-center justify-center overflow-hidden relative"
            style={{ 
              boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.2)',
            }}
          >
            {effectiveProfile.photo_file_id ? (
              <img src={effectiveProfile.photo_file_id} alt={effectiveProfile.name || 'User'} className="w-full h-full object-cover" />
            ) : (
              <span style={{ color: '#FFFFFF', fontSize: '64px' }}>
                {(effectiveProfile.name || 'U').charAt(0).toUpperCase()}
              </span>
            )}
            
            {/* Star overlay on profile picture */}
            {isBoosted && (
              <div 
                className="absolute top-2 right-2"
                style={{
                  filter: 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.8))',
                }}
              >
                <Sparkles className="w-8 h-8" style={{ color: '#FFA500' }} strokeWidth={2} />
              </div>
            )}
            
            {/* Plus icon at bottom right */}
            <div 
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full border-2 flex items-center justify-center"
              style={{
                background: 'rgba(0, 255, 136, 0.9)',
                borderColor: '#FFFFFF',
              }}
            >
              <span style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 'bold' }}>+</span>
            </div>
          </div>
        </div>

        {/* Name */}
        <h2 className="mb-3" style={{ color: '#FFFFFF', fontSize: '16px' }}>
          {effectiveProfile.name ? `@ ${effectiveProfile.name}` : '@ Name'}
        </h2>

        {isLoadingProfile && (
          <div className="mb-2 text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Оновлюємо дані профілю...
          </div>
        )}

        {/* Role Chips */}
        <div className="flex flex-wrap gap-2 justify-center mb-4">
          {rolesArray.map(role => (
            <div 
              key={role}
              className="px-3 py-1 rounded-full"
              style={{
                background: 'rgba(0, 255, 136, 1)',
                color: '#000000',
                fontSize: '12px',
                fontWeight: '500',
              }}
            >
              {ROLE_LABELS[role] || role}
            </div>
          ))}
        </div>
        
        {/* Bio */}
        {effectiveProfile.bio && effectiveProfile.bio !== 'Не вказано' && (
          <p className="mb-1" style={{ color: '#FFFFFF', fontSize: '14px' }}>
            {effectiveProfile.bio}
          </p>
        )}
        
        {/* City */}
        {effectiveProfile.city && effectiveProfile.city !== 'Не вказано' && (
          <p className="mb-4" style={{ color: '#FFFFFF', fontSize: '14px' }}>
            {effectiveProfile.city}
          </p>
        )}

        {/* Hashtags */}
        {hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1 justify-center mb-6">
            {hashtags.map((tag, idx) => (
              <span key={idx} style={{ color: '#FFFFFF', fontSize: '14px' }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Social Links */}
        {effectiveProfile.social_links && (
          <div className="flex justify-center gap-4 my-4">
            {effectiveProfile.social_links.instagram && (
              <a href={`https://instagram.com/${effectiveProfile.social_links.instagram}`} target="_blank" rel="noopener noreferrer">
                <img src="/assets/instagram.svg" alt="Instagram" className="w-6 h-6" />
              </a>
            )}
            {effectiveProfile.social_links.soundcloud && (
              <a href={`https://soundcloud.com/${effectiveProfile.social_links.soundcloud}`} target="_blank" rel="noopener noreferrer">
                <img src="/assets/soundcloud.svg" alt="SoundCloud" className="w-6 h-6" />
              </a>
            )}
            {effectiveProfile.social_links.spotify && (
              <a href={effectiveProfile.social_links.spotify} target="_blank" rel="noopener noreferrer">
                <img src="/assets/spotify.svg" alt="Spotify" className="w-6 h-6" />
              </a>
            )}
            {effectiveProfile.social_links.youtube && (
              <a href={effectiveProfile.social_links.youtube} target="_blank" rel="noopener noreferrer">
                <img src="/assets/youtube.svg" alt="YouTube" className="w-6 h-6" />
              </a>
            )}
          </div>
        )}

        {/* Stats Row */}
        <div className="flex items-center justify-center mt-4 px-6 py-3"
             style={{
               background: 'rgba(255, 255, 255, 0.05)',
               borderRadius: '12px',
             }}>
          {/* Stats with dividers */}
          <div className="flex gap-6">
            <div className="text-center min-w-[72px]">
              <div style={{ color: '#00FF88', fontSize: '24px', fontWeight: 'bold' }}>
                {isLoadingProfile ? '...' : totalWorks}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                Робіт
              </div>
            </div>
            <div className="text-center border-l min-w-[72px]" style={{ borderColor: 'rgba(255, 255, 255, 0.2)', paddingLeft: '24px' }}>
              <div style={{ color: '#00FF88', fontSize: '24px', fontWeight: 'bold' }}>
                {isLoadingProfile ? '...' : totalLikes}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                Лайків
              </div>
            </div>
            <div className="text-center border-l min-w-[72px]" style={{ borderColor: 'rgba(255, 255, 255, 0.2)', paddingLeft: '24px' }}>
              <div style={{ color: '#00FF88', fontSize: '24px', fontWeight: 'bold' }}>
                {isLoadingProfile ? '...' : followers}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                Фоловерів
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Grid - mini marketplace-style cards */}
      {portfolios && portfolios.length > 0 && (
        <div className="mt-6">
          <div className="grid grid-cols-2 gap-1 px-4">
            {portfolios.map((item, index) => (
              <PortfolioMiniCard
                key={index}
                item={item}
                // Always open the card modal, even for links (no auto-redirect)
                onClick={() => setSelectedPortfolioItem(item)}
              />
            ))}
          </div>
        </div>
      )}
      {selectedPortfolioItem && (
        <PortfolioSwipeCardModal
          item={selectedPortfolioItem}
          owner={{
            id: effectiveProfile.telegram_id ?? profile.telegram_id,
            name: effectiveProfile.name,
            username: effectiveProfile.username ?? profile.username ?? null,
            avatar: effectiveProfile.photo_file_id ?? null,
          }}
          onClose={() => setSelectedPortfolioItem(null)}
          isOwnProfile={true}
          onEdit={async (item) => {
            // TODO: Implement edit flow
            console.log('Edit item:', item);
            alert('Редагування поки не реалізовано');
          }}
          onDelete={async () => {
            try {
              // Call API to delete
              await api.profile.delete();
              // Refresh portfolio
              const response = await api.market.getProfile(profile.telegram_id);
              setMarketProfile(response);
              setSelectedPortfolioItem(null);
            } catch (error) {
              console.error('Failed to delete portfolio item:', error);
              alert('Не вдалося видалити');
            }
          }}
          onWrite={onWrite}
        />
      )}
    </div>
  );
}

